import React from 'react';
import { Alert } from '@mui/material';
import { Controller } from 'react-hook-form';
import type { UseFormRegister, FieldErrors, Control } from 'react-hook-form';
import { Button, Input, Select } from '../../../ui';
import { ErrorIcon } from '../../../ui/icons';
import { assetUrl } from '../../../../utils/webConfig';
import type { Address2FormValues } from './useAddress2Step';
import { states } from '../../../../utils/statesConstant';

const stateOptions = states.map((s) => ({ value: s.value, label: s.name }));

type AuthAddress2StepViewProps = {
    register: UseFormRegister<Address2FormValues>;
    control: Control<Address2FormValues>;
    errors: FieldErrors<Address2FormValues>;
    loading?: boolean;
    isUS: boolean;
    onSubmit?: React.FormEventHandler<HTMLFormElement>;
    error?: string | null;
    dismissError?: () => void;
};

export const AuthAddress2StepView: React.FC<AuthAddress2StepViewProps> = ({
    register,
    control,
    errors,
    loading,
    isUS,
    onSubmit,
    error,
    dismissError,
}) => (
    <form id="auth-address-details-form" onSubmit={onSubmit}>
        <div
            id="auth-address-details-step"
            className="flex flex-col mt-8 w-full min-h-[412px]"
        >
            <div
                id="auth-address-details-heading"
                className="text-body-large text-primary mb-1"
            >
                Billing Address
            </div>
            <div
                id="auth-address-details-security-message"
                className="flex items-center gap-2 mb-6 text-body-xsmall text-secondary"
            >
                <img
                    id="auth-address-details-security-lock-icon"
                    src={assetUrl('/images/svg/lock-icon.svg')}
                    alt=""
                    width={12}
                    height={14}
                />
                Your information is secure
            </div>
            <Input
                id="auth-address-city"
                {...register('city', {
                    required: '*Please enter a valid city',
                })}
                label="City"
                className="mb-3"
                error={!!errors.city}
                helperText={errors.city?.message}
            />
            {isUS ? (
                <Controller
                    name="state"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                        <Select
                            id="auth-address-state"
                            {...field}
                            label="State/Province"
                            options={stateOptions}
                            className="mb-3"
                            error={!!errors.state}
                            helperText={
                                errors.state
                                    ? '*Please select a state'
                                    : undefined
                            }
                        />
                    )}
                />
            ) : (
                <Input
                    id="auth-address-state"
                    {...register('state', {
                        required: '*Please enter a valid state/province',
                    })}
                    label="State/Province"
                    className="mb-3"
                    error={!!errors.state}
                    helperText={errors.state?.message}
                />
            )}
            {error ? (
                <Alert
                    id="auth-address-details-error-alert"
                    className="mt-6"
                    severity="error"
                    onClose={dismissError}
                    iconMapping={{ error: <ErrorIcon /> }}
                >
                    {error}
                </Alert>
            ) : null}
            <Button
                id="auth-address-details-next-button"
                type="submit"
                className="w-full mt-auto"
                loading={loading}
            >
                Next
            </Button>
        </div>
    </form>
);
